import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EnquiryForm } from "../components/EnquiryForm";
import { track } from "../lib/analytics";
import { Seo } from "../components/Seo";
import { TripDisplay } from "../components/TripDisplay";
import { GenerationProgress } from "../components/GenerationProgress";
import { api, formatApiErrorDetail } from "../lib/api";
import { CityAutocomplete } from "../components/CityAutocomplete";
import { Sparkles, Shuffle, Loader2, Bookmark, Plus, X, Moon } from "lucide-react";

const BUDGETS = ["Budget (backpacker)", "Mid-range", "Comfortable", "Luxury"];
const MONTHS = ["Flexible", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = ["Flexible", String(CURRENT_YEAR), String(CURRENT_YEAR + 1), String(CURRENT_YEAR + 2), String(CURRENT_YEAR + 3)];
const CABIN_CLASSES = ["Economy", "Premium Economy", "Business", "First"];

export default function Planner() {
  const [stops, setStops] = useState([{ city: "", nights: 3 }]);
  const [budget, setBudget] = useState("Mid-range");
  const [targetBudget, setTargetBudget] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [accessibility, setAccessibility] = useState("");
  const [interests, setInterests] = useState("");
  const [travelMonth, setTravelMonth] = useState("Flexible");
  const [travelYear, setTravelYear] = useState("Flexible");
  const [travelStartDate, setTravelStartDate] = useState("");
  const [origin, setOrigin] = useState("");
  const [originAirport, setOriginAirport] = useState("");
  const [cabinClass, setCabinClass] = useState("Economy");
  const [surprise, setSurprise] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trip, setTrip] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const setStopField = (i, key, val) =>
    setStops((prev) => prev.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addStop = () => setStops((prev) => [...prev, { city: "", nights: 2 }]);
  const removeStop = (i) => setStops((prev) => prev.filter((_, idx) => idx !== i));

  const buildTravelers = () => {
    const parts = [];
    const a = Number(adults) || 0;
    const c = Number(children) || 0;
    if (a > 0) parts.push(`${a} adult${a > 1 ? "s" : ""}`);
    if (c > 0) parts.push(`${c} child${c > 1 ? "ren" : ""}`);
    return parts.join(", ") || "1 adult";
  };

  const generate = async (isSurprise) => {
    if (!origin.trim()) {
      toast.error("Please tell us which country you're flying from so we can estimate flights.");
      return;
    }
    setLoading(true);
    setTrip(null);
    setSavedId(null);
    try {
      const valid = stops.filter((s) => s.city.trim());
      const totalNights = stops.reduce((n, s) => n + (Number(s.nights) || 0), 0) || 3;
      const { data } = await api.post("/trips/generate", {
        destination: isSurprise ? "" : valid.map((s) => s.city.trim()).join(" and "),
        stops: isSurprise ? [] : valid.map((s) => ({ city: s.city.trim(), nights: Number(s.nights) || 1 })),
        budget,
        days: totalNights,
        travelers: buildTravelers(),
        has_children: (Number(children) || 0) > 0,
        accessibility,
        interests,
        target_budget: targetBudget,
        travel_month: travelMonth === "Flexible" ? "" : travelMonth,
        travel_year: travelYear === "Flexible" ? "" : travelYear,
        travel_start_date: travelStartDate,
        origin: origin.trim(),
        origin_airport: originAirport.trim(),
        cabin_class: cabinClass,
        surprise: !!isSurprise,
      });
      const jobId = data.job_id;

      const result = await new Promise((resolve, reject) => {
        const start = Date.now();
        const poll = async () => {
          try {
            const { data: job } = await api.get(`/trips/job/${jobId}`);
            if (job.status === "done") return resolve(job.trip);
            if (job.status === "error") return reject(new Error(job.error || "Generation failed"));
            if (Date.now() - start > 240000) return reject(new Error("Timed out"));
            setTimeout(poll, 2500);
          } catch (e) {
            reject(e);
          }
        };
        poll();
      });

      setTrip(result);
      track("itinerary_generated", { destination: result?.destination || "", days: result?.itinerary?.length || 0 });
      toast.success("Your trip is ready!");
      setTimeout(() => window.scrollTo({ top: 520, behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/trips", { trip });
      setSavedId(data.id);
      track("trip_saved", { destination: trip?.destination || "" });
      toast.success("Trip saved — a copy has been emailed to you ✓");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const totalNights = stops.reduce((n, s) => n + (Number(s.nights) || 0), 0);

  return (
    <div className="min-h-screen grain relative">
      <Seo title="Plan a trip" description="Build a personalised AI itinerary with verified places, live map routes and a budget that fits — then save and share it." path="/plan" noindex />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-2">Plan a trip</h1>
        <p className="text-muted-foreground mb-10">
          Add one or more cities and set how many nights in each — or let us surprise you.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-8 rounded-3xl border border-black/5 bg-white/60">
          <div className="lg:col-span-2">
            <label className="text-sm font-medium mb-2 block">Where to? Add cities & nights</label>
            <div className="space-y-3">
              {stops.map((s, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`stop-row-${i}`}>
                  <div className="flex-1">
                    <CityAutocomplete
                      value={s.city}
                      onChange={(v) => {
                        setStopField(i, "city", v);
                        setSurprise(false);
                      }}
                      placeholder={i === 0 ? "Start typing — e.g. Kyoto, Japan" : "Add another city"}
                      testId={`plan-city-${i}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="relative">
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={s.nights}
                        data-testid={`plan-nights-${i}`}
                        onChange={(e) => setStopField(i, "nights", e.target.value)}
                        className="w-24 pl-3 pr-12 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">nights</span>
                    </div>
                    {stops.length > 1 && (
                      <button
                        type="button"
                        data-testid={`remove-stop-${i}`}
                        onClick={() => removeStop(i)}
                        className="p-2 rounded-full text-muted-foreground hover:text-[#C8553D] hover:bg-[#F3EFE8] transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                data-testid="add-stop-btn"
                onClick={addStop}
                className="inline-flex items-center gap-1.5 text-sm text-[#1A3B2B] hover:text-[#C8553D] transition-colors"
              >
                <Plus className="w-4 h-4" /> Add another city
              </button>
              <span className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="total-nights">
                <Moon className="w-4 h-4" /> {totalNights} night{totalNights !== 1 ? "s" : ""} total
              </span>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Budget</label>
            <select
              data-testid="plan-budget"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            >
              {BUDGETS.map((b) => (
                <option key={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Target budget (optional)</label>
            <input
              data-testid="plan-target-budget"
              value={targetBudget}
              onChange={(e) => setTargetBudget(e.target.value)}
              placeholder="e.g. $2000 total — we'll flag if the estimate goes over"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Adults</label>
            <input
              data-testid="plan-adults"
              type="number"
              min={1}
              max={20}
              value={adults}
              onChange={(e) => setAdults(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Children</label>
            <input
              data-testid="plan-children"
              type="number"
              min={0}
              max={20}
              value={children}
              onChange={(e) => setChildren(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">When are you travelling?</label>
            <div className="grid grid-cols-2 gap-3">
              <select
                data-testid="plan-month"
                value={travelMonth}
                onChange={(e) => setTravelMonth(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
              >
                {MONTHS.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              <select
                data-testid="plan-year"
                value={travelYear}
                onChange={(e) => setTravelYear(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
              >
                {YEARS.map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Exact start date (optional)</label>
            <input
              type="date"
              data-testid="plan-start-date"
              value={travelStartDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setTravelStartDate(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
            <p className="text-xs text-muted-foreground mt-1.5">Pick a date and we'll line the plan up with real weekdays, opening hours and events.</p>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Flying from (country) <span className="text-[#C8553D]">*</span></label>
            <input
              data-testid="plan-origin"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder="e.g. Australia, United Kingdom, UAE…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
            <p className="text-xs text-muted-foreground mt-1.5">Tells us where you're departing from so we can estimate round-trip flight prices.</p>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Departure city / airport (optional)</label>
            <input
              data-testid="plan-origin-airport"
              value={originAirport}
              onChange={(e) => setOriginAirport(e.target.value)}
              placeholder="e.g. Sydney (SYD), London Heathrow…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
            <p className="text-xs text-muted-foreground mt-1.5">Add your home city or airport for a sharper flight estimate.</p>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Cabin class</label>
            <select
              data-testid="plan-cabin-class"
              value={cabinClass}
              onChange={(e) => setCabinClass(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            >
              {CABIN_CLASSES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1.5">We'll estimate flight prices for this cabin class.</p>
          </div>
          <div className="lg:col-span-2">
            <label className="text-sm font-medium mb-2 block">Interests (optional)</label>
            <input
              data-testid="plan-interests"
              value={interests}
              onChange={(e) => setInterests(e.target.value)}
              placeholder="food, hiking, museums, nightlife…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
          </div>
          <div className="lg:col-span-2">
            <label className="text-sm font-medium mb-2 block">Accessibility needs (optional)</label>
            <input
              data-testid="plan-accessibility"
              value={accessibility}
              onChange={(e) => setAccessibility(e.target.value)}
              placeholder="e.g. wheelchair access, step-free routes, travelling with a stroller…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
            />
          </div>

          <div className="lg:col-span-2 flex flex-wrap gap-4 pt-2">
            <button
              data-testid="plan-generate-btn"
              onClick={() => generate(false)}
              disabled={loading || stops.every((s) => !s.city.trim())}
              className="px-7 py-3.5 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading && !surprise ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate trip
            </button>
            <button
              data-testid="plan-surprise-btn"
              onClick={() => {
                setSurprise(true);
                generate(true);
              }}
              disabled={loading}
              className="px-7 py-3.5 rounded-full border border-[#C8553D] text-[#C8553D] hover:bg-[#C8553D] hover:text-[#FDFBF7] transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading && surprise ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
              Surprise me
            </button>
          </div>
        </div>

        {loading && <GenerationProgress />}

        {trip && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-16"
          >
            <div className="no-print flex justify-end mb-6">
              <button
                data-testid="plan-save-btn"
                onClick={save}
                disabled={saving}
                className="px-6 py-3 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
                Save trip
              </button>
            </div>
            <TripDisplay trip={trip} editable onTripChange={setTrip} />
            <div className="mt-12">
              <EnquiryForm destination={trip.destination || ""} sourceTripId={savedId} startDate={travelStartDate} partySize={Number(adults) + Number(children)} />
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}
